import { motion } from "framer-motion";
import { Loader2, Sparkles, Circle, Clapperboard } from "lucide-react";
import { PRODUCTION_JOURNEY } from "@/lib/studio";

export const cx = (...c) => c.filter(Boolean).join(" ");

/* ---------------------------------------------------------------
   Cinematic surfaces
   --------------------------------------------------------------- */

export function GlassCard({ className = "", hover = false, children, ...props }) {
  return (
    <div
      className={cx(
        "cine-panel transition-all duration-300",
        hover && "hover:border-[#5B4DFF]/50 hover:glow-purple hover:-translate-y-0.5 cursor-pointer",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

// Alias with a film-studio name (used by redesigned screens).
export const FilmPanel = GlassCard;

// A panel with a darkened, scrimmed cinematic photo behind it.
export function RoomBackdrop({ image, className = "", children, testid }) {
  return (
    <div className={cx("room-backdrop cine-panel cine-sweep", className)} data-testid={testid}>
      <div className="room-img" style={{ backgroundImage: `url(${image})` }} />
      <div className="room-scrim" />
      <div className="room-body">{children}</div>
    </div>
  );
}

/* ---------------------------------------------------------------
   Full-bleed cinematic stage wrapper (atmosphere layers)
   --------------------------------------------------------------- */
export function CinematicStage({ children, className = "" }) {
  return (
    <div className={cx("cine-stage relative", className)}>
      {children}
    </div>
  );
}

/* ---------------------------------------------------------------
   Stage header — every screen is a "room" of the studio
   --------------------------------------------------------------- */
export function StageHeader({ stage, room, title, subtitle, actions, icon: Icon = Clapperboard }) {
  return (
    <div className="mb-8 stage-in">
      <div className="flex items-center gap-3 mb-3">
        <span className="grid place-items-center w-9 h-9 rounded-xl bg-[#5B4DFF]/15 border border-[#5B4DFF]/30 text-[#b6aaff]">
          <Icon className="w-4 h-4" />
        </span>
        {stage && <span className="stage-num text-[11px] text-[#FFB300]/80">STAGE {stage}</span>}
        {room && (
          <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-slate-400">
            {room}
          </span>
        )}
      </div>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white leading-[1.05]">
            {title}
          </h1>
          {subtitle && <p className="text-slate-400 mt-2 max-w-2xl text-sm sm:text-base">{subtitle}</p>}
        </div>
        {actions && <div className="flex items-center gap-3 shrink-0">{actions}</div>}
      </div>
      <div className="mt-5 h-px w-full bg-gradient-to-r from-[#5B4DFF]/50 via-white/5 to-transparent" />
    </div>
  );
}

// Backward-compatible original header (kept so every page still works).
export function PageHeader({ eyebrow, title, subtitle, actions }) {
  return <StageHeader room={eyebrow} title={title} subtitle={subtitle} actions={actions} />;
}

/* ---------------------------------------------------------------
   The production journey rail — the spine of the film studio
   --------------------------------------------------------------- */
export function PipelineRail({ activeKey, doneKeys = [], compact = false, onSelect }) {
  return (
    <div className={cx("relative overflow-x-auto no-scrollbar", compact ? "py-2" : "py-3")}>
      <div className="flex items-center gap-0 min-w-max">
        {PRODUCTION_JOURNEY.map((s, i) => {
          const done = doneKeys.includes(s.key);
          const active = s.key === activeKey;
          return (
            <div key={s.key} className="flex items-center">
              <button
                type="button"
                onClick={() => onSelect && onSelect(s.key)}
                data-testid={`rail-${s.key}`}
                className={cx(
                  "group flex flex-col items-center gap-1.5 px-1",
                  onSelect ? "cursor-pointer" : "cursor-default"
                )}
              >
                <span
                  className={cx(
                    "grid place-items-center rounded-full border transition-all",
                    compact ? "w-6 h-6" : "w-8 h-8",
                    active
                      ? "bg-[#FFB300] border-[#FFB300] text-[#0B0B12] shadow-[0_0_18px_rgba(255,179,0,0.6)]"
                      : done
                      ? "bg-[#5B4DFF] border-[#5B4DFF] text-white"
                      : "bg-white/5 border-white/12 text-slate-500 group-hover:border-[#5B4DFF]/40"
                  )}
                >
                  {done && !active ? (
                    <Sparkles className="w-3 h-3" />
                  ) : (
                    <span className="text-[10px] font-mono font-bold">{i + 1}</span>
                  )}
                </span>
                <span
                  className={cx(
                    "text-[10px] font-mono tracking-wide whitespace-nowrap",
                    active ? "text-[#FFB300]" : done ? "text-slate-300" : "text-slate-600"
                  )}
                >
                  {s.label}
                </span>
              </button>
              {i < PRODUCTION_JOURNEY.length - 1 && (
                <span
                  className={cx(
                    "h-px w-6 sm:w-8 shrink-0 -mt-4",
                    done ? "rail-line" : "bg-white/10"
                  )}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------
   Pills, status, badges
   --------------------------------------------------------------- */
export function Pill({ children, tone = "purple", className = "", ...rest }) {
  const tones = {
    purple: "bg-[#5B4DFF]/15 text-[#b6aaff] border-[#5B4DFF]/30",
    gold: "bg-[#FFB300]/15 text-[#FFB300] border-[#FFB300]/30",
    green: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    slate: "bg-white/5 text-slate-300 border-white/10",
    red: "bg-red-500/15 text-red-300 border-red-500/30",
    amber: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  };
  return (
    <span {...rest} className={cx("inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border", tones[tone] || tones.purple, className)}>
      {children}
    </span>
  );
}

// Clearly-labelled demo/sample marker (never mixed with real user data).
export function DemoBadge({ className = "" }) {
  return (
    <span className={cx("inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-mono uppercase tracking-wider bg-black/50 text-amber-300 border border-amber-400/40 backdrop-blur", className)}>
      <Circle className="w-2 h-2 fill-amber-400 text-amber-400" /> Demo
    </span>
  );
}

export function Spinner({ label }) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-20 text-slate-400">
      <div className="relative">
        <div className="w-12 h-12 rounded-full border-2 border-[#5B4DFF]/20" />
        <Loader2 className="w-12 h-12 animate-spin text-[#5B4DFF] absolute inset-0" />
        <Clapperboard className="w-4 h-4 text-[#FFB300] absolute inset-0 m-auto" />
      </div>
      {label && <span className="font-mono text-xs tracking-wide">{label}</span>}
    </div>
  );
}

export function EmptyState({ icon: Icon = Sparkles, title, subtitle, action, image }) {
  const inner = (
    <div className="p-12 sm:p-16 text-center flex flex-col items-center">
      <div className="w-16 h-16 rounded-2xl bg-[#5B4DFF]/15 border border-[#5B4DFF]/30 grid place-items-center mb-5 pulse-ring">
        <Icon className="w-7 h-7 text-[#b6aaff]" />
      </div>
      <h3 className="font-display text-xl sm:text-2xl font-bold text-white">{title}</h3>
      {subtitle && <p className="text-slate-400 mt-2 max-w-md">{subtitle}</p>}
      {action && <div className="mt-7">{action}</div>}
    </div>
  );
  if (image) {
    return <RoomBackdrop image={image} className="overflow-hidden">{inner}</RoomBackdrop>;
  }
  return <GlassCard className="overflow-hidden cine-sweep">{inner}</GlassCard>;
}

export function StatCard({ label, value, icon: Icon, tone = "purple" }) {
  const c = tone === "gold" ? "#FFB300" : "#5B4DFF";
  return (
    <GlassCard hover className="p-5 relative overflow-hidden">
      <div
        className="absolute -bottom-6 -right-6 w-20 h-20 rounded-full blur-2xl opacity-30"
        style={{ background: c }}
      />
      <div className="relative flex items-center justify-between">
        <div className="font-mono text-[11px] uppercase tracking-wider text-slate-400">{label}</div>
        {Icon && <Icon className="w-4 h-4" style={{ color: c }} />}
      </div>
      <div className="relative mt-3 font-display text-3xl font-bold text-white">{value}</div>
    </GlassCard>
  );
}

export const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06 } },
};
export const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
};

export const MotionCard = motion.create(GlassCard);
